import { z } from 'zod';

import { Currency } from '../enums/payments';
import { TrainerFeeType } from '../enums/trainer-fee';
import { isExactMinor } from '../utils/money';

/**
 * Upper bound of a trainer fee value. Deliberately its own constant, equal
 * to `MEMBERSHIP_PRICE_MAX` but not imported from `./membership`: the
 * membership DTOs compose these fragments, and importing back would close a
 * module cycle.
 */
export const TRAINER_FEE_MAX = 1_000_000_000;

// An amount or a percent, both with at most two decimals. More decimals are
// rejected, never rounded. Every supported currency has two minor digits, so
// EUR stands for "two decimals" here, for amounts and percents alike.
const TrainerFeeValueDto = z
  .number()
  .nonnegative('VALIDATION__NonNegative')
  .max(TRAINER_FEE_MAX, 'VALIDATION__Max')
  .refine(
    (value) => isExactMinor(value, Currency.EUR),
    'VALIDATION__InvalidAmount',
  );

/**
 * The fee pair spread into a DTO object. Absent fields = "not touched";
 * a null pair = "no fee"; zero is an explicit fee. Compose with
 * `.superRefine(refineTrainerFee)` on the enclosing object.
 */
export const trainerFeeFields = {
  trainerFeeType: z.nativeEnum(TrainerFeeType).nullable().optional(),
  trainerFeeValue: TrainerFeeValueDto.nullable().optional(),
};

/**
 * Pair rule: both absent, both null, or both set — and a percent never
 * above 100.
 */
export function refineTrainerFee(
  value: {
    trainerFeeType?: TrainerFeeType | null;
    trainerFeeValue?: number | null;
  },
  ctx: z.RefinementCtx,
) {
  const type = value.trainerFeeType;
  const fee = value.trainerFeeValue;
  if (
    (type === undefined && fee === undefined) ||
    (type === null && fee === null)
  )
    return;
  if (type == null || fee == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [type == null ? 'trainerFeeType' : 'trainerFeeValue'],
      message: 'VALIDATION__Required',
    });
  } else if (type === TrainerFeeType.PERCENT && fee > 100) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['trainerFeeValue'],
      message: 'VALIDATION__Max',
    });
  }
}

/**
 * One trainer's fee on a group session: FIXED only (PO 2026-10-10), in the
 * session's currency. `null` clears it, zero stays an explicit zero. Strict,
 * so an attempted type override is rejected rather than ignored.
 */
export const SessionTrainerFeeDto = z
  .object({
    trainerUserId: z.string().uuid(),
    value: TrainerFeeValueDto.nullable(),
  })
  .strict();

export type SessionTrainerFeeInput = z.infer<typeof SessionTrainerFeeDto>;

/**
 * List rule for a DTO carrying session/template trainer fees: one entry per
 * trainer, and, when the same request also sends the trainer set, every fee
 * names a trainer in it. Without `trainerUserIds` (a sparse edit) the
 * service checks the fees against the set the row ends up with.
 */
export function refineSessionTrainerFees(
  value: {
    trainerUserIds?: string[];
    trainerFees?: { trainerUserId: string }[];
  },
  ctx: z.RefinementCtx,
) {
  if (!value.trainerFees) return;
  const seen = new Set<string>();
  const trainers = value.trainerUserIds && new Set(value.trainerUserIds);
  value.trainerFees.forEach(({ trainerUserId }, index) => {
    const path = ['trainerFees', index, 'trainerUserId'];
    if (seen.has(trainerUserId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path,
        message: 'VALIDATION__DuplicateIds',
      });
    }
    seen.add(trainerUserId);
    if (trainers && !trainers.has(trainerUserId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path,
        message: 'VALIDATION__NotAllowed',
      });
    }
  });
}
